import { useSearchParams } from "react-router-dom";

import { useOrder } from "./queries/orders";

type SplitPaymentFlowReturn = {
  orderId: string | null;
  isActive: boolean;
  unitReceived: number;
  loading: boolean;
  reset: () => void;
};

/**
 * Hook to be used to find out information about the split payment,
 * e.g. whether it is active and how much cash has already been received
 */
export const useSplitPaymentFlow = (): SplitPaymentFlowReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderIdFromParams = searchParams.get("orderId");

  const { data: order, status: orderStatus } = useOrder(orderIdFromParams);

  const reset = () => {
    setSearchParams((params) => {
      params.delete("orderId");
      return params;
    });
  };

  if (!orderIdFromParams) {
    return {
      orderId: null,
      isActive: false,
      unitReceived: 0,
      loading: false,
      reset
    };
  }

  // When the previous payment completed with skipReceipt the URL will have
  // skipReceipt=true. In that case there is no active split payment and we
  // must not block the next customer's pay buttons while the old order loads.
  const skipReceiptParam = searchParams.get("skipReceipt");
  if (skipReceiptParam === "true") {
    return {
      orderId: orderIdFromParams,
      isActive: false,
      unitReceived: 0,
      loading: false,
      reset
    };
  }

  if (orderStatus !== "success") {
    return {
      orderId: orderIdFromParams,
      isActive: false,
      unitReceived: 0,
      loading: true,
      reset
    };
  }

  if (order.status !== "pending") {
    return {
      orderId: orderIdFromParams,
      isActive: false,
      unitReceived: 0,
      loading: false,
      reset
    };
  }

  const unitReceived =
    order.payments
      ?.filter((p) => p.paymentProvider === "cash" && p.status === "paid")
      .reduce((acc, payment) => {
        return acc + payment.unitPrice;
      }, 0) ?? 0;

  return {
    orderId: orderIdFromParams,
    isActive: unitReceived !== 0,
    unitReceived,
    loading: false,
    reset
  };
};
